import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import React, { useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import Toast from 'react-native-toast-message';
import CustomButton from '../../../components/CustomButton';
import DeleteModal from '../../../components/DeleteModal';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import MyDataTable from '../../../components/MyDataTable';
import { Pagination } from '../../../components/Pagination';
import { ClientError } from '../../../services/central-api/base-service';
import { dispatchService } from '../../../services/central-api/dispatch';
import { translate } from '../../../services/translate';
import { DispatchParamList } from '../../../typings/Params';
import { useAuth } from '../../../context/Auth';
import Filter from './Filters';
import DropdownButton from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';

export default function CityProperties({ navigation }: { navigation: Navigation; }) {
    const { user: authenticatedUser } = useAuth();
    const route = useRoute<RouteProp<DispatchParamList, 'CityProperties'>>();

    const [filters, setFilters] = useFilters<DispatchParamList['CityProperties']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        actingBodyId: route.params.actingBodyId ?? undefined,
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(route.params.textFilter ?? '');

    const [selectedCityProperty, setSelectedCityProperty] = useState<CityProperty>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getPaginatedCityProperties', filters],
        queryFn: () => dispatchService.getPaginatedCityProperties(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    async function confirmDelete(cityProperty: CityProperty) {
        try {
            if (cityProperty?.id) {
                await dispatchService.deleteCityProperty(cityProperty.id);
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                setDeleteModalVisible(false);
                refetch();
            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return <Container>
        <Header
            title={translate('cityProperties')}
            actions={
                <CustomButton
                    text={translate('register')}
                    onPress={() => {
                        setSelectedCityProperty(undefined);
                        navigation.navigate('CityPropertyForm');
                    }}
                />
            }
        />
        <Card>
            <DataTableHeader
                zIndex={2}
                title={translate('registeredCityProperties')}
                filter={<Filter
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter}
                />}
                filterInput={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                    onKeyPress={handleKeyDown}
                />}
            />

            <DataTableContainer
                dataTable={<MyDataTable
                    progressPending={isLoading}
                    columns={[
                        {
                            name: 'ID',
                            selector: row => row.id,
                            grow: 0
                        },
                        {
                            name: translate('name'),
                            selector: row => row.name,
                            wrap: true,
                            grow: 1
                        },
                        {
                            name: translate('actingBody'),
                            selector: row => row.actingBody.name,
                            wrap: true,
                        },
                        {
                            cell: row => (
                                <DropdownButton
                                    actions={[
                                        {
                                            content: translate('editInformation'),
                                            onPress: () => {
                                                setSelectedCityProperty(row);
                                                navigation.navigate('CityPropertyForm', { id: row.id });
                                            }
                                        },
                                        {
                                            content: translate('delete'),
                                            variant: 'danger',
                                            onPress: () => {
                                                setSelectedCityProperty(row);
                                                setDeleteModalVisible(true);
                                            },
                                        }
                                    ]}
                                />
                            ),
                            wrap: true,
                            button: true,
                        }
                    ]}
                    data={data?.rows ?? []}
                />}
                pagination={<Pagination
                    totalItems={data?.count ?? 0}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));
                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));
                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>

        {selectedCityProperty &&
            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={selectedCityProperty.name}
                onSubmit={async () => {
                    await confirmDelete(selectedCityProperty);
                }}
            />
        }

    </Container>;
}
